import React, {
    FC,
    memo,
    ReactElement,
    useCallback,
    useEffect,
    useRef,
    useState,
    useTransition,
} from 'react'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import Tonal from '@sport1/news-styleguide/Tonal'
import { LabelProps, TagProps } from '@sport1/types/web'
import BurgerMenuContent from '@/components/BurgerMenu/BurgerMenuContent'
import { useNavigation } from '@/utils/navigation/Standard'
import { useTracking } from '@/utils/tracking/TrackingProvider'
import { runOnRaf } from '@/utils/webVitals'

type BurgerMenuProps = {
    isBurgerMenuOpen: boolean
    initialTag?: TagProps | LabelProps
    onStateChange: (isBurgerMenuOpen: boolean) => void
}

export const BurgerMenu: FC<BurgerMenuProps> = ({
    isBurgerMenuOpen,
    initialTag,
    onStateChange,
}): ReactElement => {
    const bodyClassName = useRef<string | undefined>(undefined)
    const animatedRef = React.useRef<HTMLDivElement | null>(null)
    const containerRef = useRef<(ReactElement & HTMLDivElement) | null>(null)
    const { navigation } = useNavigation()
    const [isOpen, setIsOpen] = useState(false)
    const { trackIvwInfo, trackIvwView } = useTracking()
    const [, startTransition] = useTransition()

    const scrollToTop = useCallback(() => {
        if (animatedRef.current) {
            const currentRef = animatedRef.current
            runOnRaf(() => {
                currentRef.scrollTop = 0
            })
        }
    }, [])

    const toggleMenu = useCallback(() => {
        const body = typeof document !== 'undefined' ? document.body : null

        if (isOpen) {
            runOnRaf(() => {
                body?.classList.remove('overflow-hidden', 'sm:overflow-auto')
            })
            startTransition(() => {
                setIsOpen(false)
                onStateChange(false)
            })
            scrollToTop()
        } else {
            runOnRaf(() => {
                bodyClassName.current = body?.className
                body?.classList.add('overflow-hidden', 'sm:overflow-auto')
            })

            startTransition(() => {
                setIsOpen(true)
                onStateChange(true)
            })
        }
    }, [isOpen, onStateChange, scrollToTop])

    const closeMenu = useCallback(() => {
        toggleMenu()
        trackIvwInfo(
            {
                ivw_context: 'close-menu',
            },
            true
        )
        trackIvwView({}, true, true)
    }, [toggleMenu, trackIvwView, trackIvwInfo])

    useEffect(() => {
        if (isOpen !== isBurgerMenuOpen) {
            toggleMenu()
        }
    }, [isBurgerMenuOpen, isOpen, toggleMenu])

    return (
        <>
            {navigation?.burgerMenuLayout && (
                <div
                    ref={animatedRef}
                    data-testid="burger-menu-container"
                    className="w-full sm:w-375 bg-white"
                    style={{
                        bottom: 0,
                        left: 0,
                        transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
                        overflowY: 'auto',
                        position: 'fixed',
                        top: 0,
                        transition: 'transform 0.2s ease-out',
                        willChange: 'transform',
                        zIndex: 2147483647,
                        backfaceVisibility: 'hidden',
                    }}
                >
                    <NonFlexingContainer
                        innerRef={containerRef}
                        pointerEvents="auto"
                        backgroundColor="pearl"
                        height="100%"
                    >
                        <BurgerMenuContent
                            isOpen={isOpen}
                            initialTag={initialTag}
                            layoutData={navigation.burgerMenuLayout}
                            toggleMenu={toggleMenu}
                            closeMenu={closeMenu}
                            scrollToTop={scrollToTop}
                        />
                    </NonFlexingContainer>
                </div>
            )}
            {isOpen ? (
                <Tonal
                    position="fixed"
                    top={0}
                    left={0}
                    bottom={0}
                    zIndex={2147483646}
                    onPress={closeMenu}
                />
            ) : null}
        </>
    )
}

export default memo(BurgerMenu)
